import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

export function downloadCSV(data: any[], filename: string) {
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => headers.map(header => `"${row[header] || ''}"`).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function downloadExcel(data: any[], filename: string) {
  // For now, we'll use CSV format since we don't have xlsx writer
  // In a real implementation, you'd use a library like xlsx to create proper Excel files
  downloadCSV(data, filename.replace('.xlsx', '.csv'));
}

export function downloadStyledExcel(data: any[], filename: string, groupedMembers?: { [key: string]: any[] }) {
  const XLSX = require('xlsx');
  
  // Create a new workbook
  const wb = XLSX.utils.book_new();
  
  // Define styles
  const styles = {
    title: {
      font: { bold: true, size: 16, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "CF2030" } },
      alignment: { horizontal: "center", vertical: "center" }
    },
    groupHeader: {
      font: { bold: true, size: 12, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "B91C2C" } },
      alignment: { horizontal: "center", vertical: "center" }
    },
    columnHeader: {
      font: { bold: true, size: 11, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "7F1D1D" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    },
    hotSeat: {
      font: { bold: true, color: { rgb: "CF2030" } },
      fill: { fgColor: { rgb: "FEF2F2" } }
    },
    normal: {
      border: {
        top: { style: "thin", color: { rgb: "E5E7EB" } },
        bottom: { style: "thin", color: { rgb: "E5E7EB" } },
        left: { style: "thin", color: { rgb: "E5E7EB" } },
        right: { style: "thin", color: { rgb: "E5E7EB" } }
      }
    }
  };

  // Prepare data with styling information
  const wsData: any[] = [];
  const wsStyles: any = {};
  const mergeRanges: any[] = [];
  let currentRow = 0;

  // Add title
  wsData.push(['BNI Hot Seat Results - Grouped by Experience']);
  wsStyles[`A${currentRow + 1}`] = styles.title;
  currentRow++;

  // Add empty row
  wsData.push([]);
  currentRow++;

  if (groupedMembers) {
    // Add column headers
    const headers = [
      'Rank', 'Member Name', 'Years in BNI', '121 Meetings', 'Ref Given', 
      'Ref Received', 'TYFCB Given', 'Revenue Received', 'Visitors', 
      'Green Score', 'Contribution Gap', 'Final Score'
    ];
    wsData.push(headers);
    
    // Style column headers
    headers.forEach((_, colIndex) => {
      const cellRef = XLSX.utils.encode_cell({ r: currentRow, c: colIndex });
      wsStyles[cellRef] = styles.columnHeader;
    });
    currentRow++;

    // Process each group
    Object.entries(groupedMembers).forEach(([groupName, groupMembers]) => {
      if (groupMembers.length === 0) return;
      
      // Add group header
      const groupHeader = `${groupName} Years Experience (${groupMembers.length} members)`;
      wsData.push([groupHeader]);
      
      // Style group header
      const groupHeaderRef = XLSX.utils.encode_cell({ r: currentRow, c: 0 });
      wsStyles[groupHeaderRef] = styles.groupHeader;
      
      // Store merge range for later application
      mergeRanges.push({
        s: { r: currentRow, c: 0 },
        e: { r: currentRow, c: headers.length - 1 }
      });
      
      currentRow++;

      // Add group members
      groupMembers.forEach((member: any) => {
        const globalIndex = data.findIndex(m => m['Member Name'] === member.memberName);
        const isHotSeat = globalIndex < 10;
        
        const row = [
          globalIndex + 1,
          member.memberName,
          member.yearsInBNI,
          member.oneOnOneMeetings,
          member.refGiven,
          member.refReceived,
          member.tyfcbGiven,
          member.revenueReceived,
          member.visitors,
          member.currScore || 0,
          member.contributionGap,
          member.finalScore.toFixed(2)
        ];
        
        wsData.push(row);
        
        // Style the row
        row.forEach((_, colIndex) => {
          const cellRef = XLSX.utils.encode_cell({ r: currentRow, c: colIndex });
          wsStyles[cellRef] = isHotSeat ? styles.hotSeat : styles.normal;
        });
        currentRow++;
      });
      
      // Add empty row between groups
      wsData.push([]);
      currentRow++;
    });
  } else {
    // Fallback to simple data export
    const headers = Object.keys(data[0]);
    wsData.push(headers);
    
    // Style headers
    headers.forEach((_, colIndex) => {
      const cellRef = XLSX.utils.encode_cell({ r: currentRow, c: colIndex });
      wsStyles[cellRef] = styles.columnHeader;
    });
    currentRow++;

    data.forEach((row, rowIndex) => {
      const rowData = headers.map(header => row[header]);
      wsData.push(rowData);
      
      // Style data rows
      headers.forEach((_, colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: currentRow, c: colIndex });
        wsStyles[cellRef] = styles.normal;
      });
      currentRow++;
    });
  }

  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  
  // Apply merge ranges
  if (mergeRanges.length > 0) {
    ws['!merges'] = mergeRanges;
  }
  
  // Apply styles
  ws['!cols'] = [
    { wch: 8 },   // Rank
    { wch: 20 },  // Member Name
    { wch: 12 },  // Years in BNI
    { wch: 12 },  // 121 Meetings
    { wch: 10 },  // Ref Given
    { wch: 12 },  // Ref Received
    { wch: 15 },  // TYFCB Given
    { wch: 15 },  // Revenue Received
    { wch: 10 },  // Visitors
    { wch: 12 },  // Green Score
    { wch: 15 },  // Contribution Gap
    { wch: 12 }   // Final Score
  ];

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Hot Seat Results');
  
  // Write and download
  XLSX.writeFile(wb, filename);
}